import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import { tracked } from '@glimmer/tracking';

import { type IntlService, t } from 'ember-intl';
import style from 'ember-style-modifier';
import { trackedFunction } from 'reactiveweb/function';

import Component from 'umg-ember-backend/components/record-detail/forms';
import ContestEntries from 'umg-ember-backend/components/record-explorer/for/contest-entry';
import Button from 'umg-ember-backend/components/ui/button';
import ContentWithSidebar from 'umg-ember-backend/components/ui/content-with-sidebar';
import Form from 'umg-ember-backend/components/ui/form';
import type FetchService from 'umg-ember-backend/services/fetch';
import { labelValueOptions } from 'umg-ember-backend/utils/intl';

import FormFields from './form-fields';

class ExportSettings {
  id = 0;
  recordType = 'contest-export-setting';
  @tracked status: number[] = [];
}

class DrawWinnersSettings {
  id = 0;
  recordType = 'contest-draw-winners-setting';
  @tracked numberOfWinners = 5;
}

export default class RecordDetailFormsContestComponent extends Component {
  // Services
  @service declare fetch: FetchService;
  @service declare intl: IntlService;

  // Defaults
  exportSettings = new ExportSettings();
  drawWinnersSettings = new DrawWinnersSettings();
  status = [1, 2, 3, 4, 5];

  // Getter and setters
  get filters() {
    return {
      contest: this.args.model.id,
    };
  }

  get fileName() {
    return `${dasherize(this.args.model.title)}-${new Date().toISOString()}`;
  }

  get statusOptions() {
    return labelValueOptions.call(this, this.status, [
      'forms.contest-entry.options.status',
    ]);
  }

  // Functions
  downloadMedia = async (model: ExportSettings) => {
    const searchParams = new URLSearchParams(
      model.status.map((status) => ['status[]', String(status)]),
    );
    const response = await this.fetch.request(
      `/contests/${this.args.model.id}/mediaExport?${searchParams}`,
      { headers: { 'Content-Type': 'text/zip' } },
    );

    const link = document.createElement('a');
    const url = URL.createObjectURL(response);

    link.setAttribute('href', url);
    link.setAttribute('download', `${this.fileName}.zip`);
    link.click();

    link.remove();
    URL.revokeObjectURL(url);
  };

  downloadCsv = async (model: ExportSettings) => {
    const searchParams = new URLSearchParams(
      model.status.map((status) => ['status[]', String(status)]),
    );
    const response = await this.fetch.request(
      `/contests/${this.args.model.id}/csvExport?${searchParams}`,
    );

    const link = document.createElement('a');
    const url = URL.createObjectURL(response);

    link.setAttribute('href', url);
    link.setAttribute('download', `${this.fileName}.csv`);
    link.click();

    link.remove();
    URL.revokeObjectURL(url);
  };

  drawWinners = async (model: DrawWinnersSettings) => {
    const response = await this.fetch.post(`/contestEntries/drawWinners`, {
      data: {
        contest: this.args.model.id,
        numberOfWinners: Number(model.numberOfWinners),
      },
    });
    await this.store.pushPayload(response);
  };

  loadLegalContext = trackedFunction(this, async () => {
    const legalContext = await this.args.model.legalContext;

    return legalContext?.id;
  });

  // Template
  <template>
    <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
      <ContentWithSidebar>
        <:content>
          <form.fieldset @titleKey='form' as |fieldset|>
            <fieldset.field @property='title' />

            <FormFields
              @form={{form}}
              @showErrors={{@showErrors}}
              @deleteRelation={{this.deleteRelation}}
              @newRecord={{this.newRecord}}
            />
          </form.fieldset>
          <form.fieldset @titleKey='confirmation' as |fieldset|>
            <fieldset.field @property='confirmationText' @type='text-editor' />
          </form.fieldset>
          {{#unless @model.isNew}}
            <div {{style height='600px' borderRadius='0px'}}>
              <ContestEntries
                @recordType='contest-entry'
                @filters={{this.filters}}
              />
            </div>
          {{/unless}}
        </:content>
        <:sidebar>
          <form.fieldset @titleKey='settings' as |fieldset|>
            <fieldset.field
              @property='cmsLanguageId'
              @type='select-value'
              @options={{this.availableLanguages}}
            />
            <fieldset.field @property='legalContext' @type='belongs-to' />
            <fieldset.field @property='terms' @type='belongs-to' />
            <fieldset.field @property='ctaButton' />
            <fieldset.field @property='keepWinners' @type='checkbox' />
            <fieldset.field
              @filters={{hash legalContext=this.loadLegalContext.value}}
              @disabled={{this.loadLegalContext.isPending}}
              @on={{hash openNewRecord=false openRecord=false}}
              @property='newsletterTarget'
              @type='belongs-to'
            />

          </form.fieldset>
          <form.fieldset @titleKey='period' as |fieldset|>
            <fieldset.field @property='startDate' @type='date-time' />
            <fieldset.field @property='endDate' @type='date-time' />
            <fieldset.field @property='deleteDate' @type='date-time' />
          </form.fieldset>
          {{#unless @model.isNew}}
            <Form
              @disableRegisterToBreadcrumb={{true}}
              @model={{this.exportSettings}}
              as |form|
            >
              <form.fieldset @titleKey='export' as |fieldset|>
                <fieldset.field
                  @property='status'
                  @type='select-multiple'
                  @options={{this.statusOptions}}
                />
                <Button
                  {{on 'click' (fn this.downloadCsv form.model)}}
                  @displayBlock={{true}}
                  @label={{t 'record_detail.forms.contest.download_csv'}}
                />

                <Button
                  {{on 'click' (fn this.downloadMedia form.model)}}
                  @displayBlock={{true}}
                  @label={{t 'record_detail.forms.contest.download_media'}}
                />
              </form.fieldset>
            </Form>
            <Form
              @disableRegisterToBreadcrumb={{true}}
              @model={{this.drawWinnersSettings}}
              as |form|
            >
              <form.fieldset @titleKey='drawWinners' as |fieldset|>
                <fieldset.field @property='numberOfWinners' @type='number' />
                <Button
                  {{on 'click' (fn this.drawWinners form.model)}}
                  @displayBlock={{true}}
                  @label={{t 'record_detail.forms.contest.draw_winner'}}
                />
              </form.fieldset>
            </Form>
          {{/unless}}
          <form.sections.meta />
        </:sidebar>
      </ContentWithSidebar>
    </Form>
  </template>
}
